import { createCheckbox } from "@gluestack-ui/core/checkbox/creator";
import { IPrimitiveIcon, PrimitiveIcon, UIIcon } from "@gluestack-ui/core/icon/creator";
import { VariantProps, tva, useStyleContext, withStyleContext } from "@gluestack-ui/utils";
import { cssInterop } from "nativewind";
import React from "react";
import type { TextProps, ViewProps } from "react-native";
import { Platform, Pressable, Text, View } from "react-native";

const IndicatorWrapper = React.forwardRef<React.ComponentRef<typeof View>, ViewProps>(({ ...props }, ref) => {
	return <View {...props} ref={ref} />;
});

const LabelWrapper = React.forwardRef<React.ComponentRef<typeof Text>, TextProps>(({ ...props }, ref) => {
	return <Text {...props} ref={ref} />;
});

const IconWrapper = React.forwardRef<any, IPrimitiveIcon>(({ ...props }, ref) => {
	return <UIIcon {...props} ref={ref} />;
});

const SCOPE = "CHECKBOX";
const UICheckbox = createCheckbox({
	// @ts-expect-error
	Root: Platform.OS === "web" ? withStyleContext(View, SCOPE) : withStyleContext(Pressable, SCOPE),
	Group: View,
	Icon: IconWrapper,
	Label: LabelWrapper,
	Indicator: IndicatorWrapper,
});

cssInterop(PrimitiveIcon, {
	className: {
		target: "style",
		nativeStyleToProp: {
			height: true,
			width: true,
			fill: true,
			color: "classNameColor",
			stroke: true,
		} as any,
	},
});

const checkboxStyle = tva({
	base: "flex-row items-start gap-2 justify-start web:cursor-pointer data-[disabled=true]:cursor-not-allowed",
	variants: {
		size: {
			xs: "",
			sm: "",
			md: "",
			lg: "",
		},
	},
});

const checkboxIndicatorStyle = tva({
	base: "justify-center mt-0.5 items-center bg-transparent rounded web:data-[focus-visible=true]:outline-none web:data-[focus-visible=true]:ring-2 border border-neutral-950 data-[checked=true]:data-[invalid=false]:bg-primary-500 data-[hover=true]:bg-transparent data-[invalid=true]:border-error-500 data-[invalid=true]:bg-error-50 data-[hover=true]:data-[invalid=true]:bg-error-50 data-[hover=true]:data-[checked=true]:data-[disabled=true]:opacity-40 data-[disabled=true]:opacity-40",
	parentVariants: {
		size: {
			lg: "w-6 h-6",
			md: "w-5 h-5",
			sm: "w-4 h-4 mt-1",
			xs: "w-3.5 h-3.5",
		},
	},
});

const checkboxLabelStyle = tva({
	base: "text-neutral-700 data-[hover=true]:data-[invalid=false]:data-[disabled=false]:text-neutral-950 data-[checked=true]:text-neutral-950 data-[invalid=true]:text-error-800 data-[disabled=true]:opacity-40 web:select-none",
	parentVariants: {
		size: {
			lg: "text-lg",
			md: "text-base",
			sm: "text-base",
			xs: "text-xs",
		},
	},
});

const checkboxIconStyle = tva({
	base: "text-neutral-950 fill-none",
	parentVariants: {
		size: {
			xs: "h-3 w-3",
			sm: "h-3 w-3",
			md: "h-4 w-4",
			lg: "h-5 w-5",
		},
	},
});

const CheckboxGroup = UICheckbox.Group;

export type ICheckboxProps = React.ComponentPropsWithoutRef<typeof UICheckbox> & VariantProps<typeof checkboxStyle>;

const Checkbox = React.forwardRef<React.ComponentRef<typeof UICheckbox>, ICheckboxProps>(
	({ className, size = "md", ...props }, ref) => {
		return (
			<UICheckbox
				className={checkboxStyle({
					class: className,
					size,
				})}
				{...props}
				context={{
					size,
				}}
				ref={ref}
			/>
		);
	},
);

type ICheckboxIndicatorProps = React.ComponentPropsWithoutRef<typeof UICheckbox.Indicator> &
	VariantProps<typeof checkboxIndicatorStyle>;

const CheckboxIndicator = React.forwardRef<React.ComponentRef<typeof UICheckbox.Indicator>, ICheckboxIndicatorProps>(
	({ className, ...props }, ref) => {
		const { size: parentSize } = useStyleContext(SCOPE);

		return (
			<UICheckbox.Indicator
				className={checkboxIndicatorStyle({
					parentVariants: {
						size: parentSize,
					},
					class: className,
				})}
				{...props}
				ref={ref}
			/>
		);
	},
);

type ICheckboxLabelProps = React.ComponentPropsWithoutRef<typeof UICheckbox.Label> &
	VariantProps<typeof checkboxLabelStyle>;
const CheckboxLabel = React.forwardRef<React.ComponentRef<typeof UICheckbox.Label>, ICheckboxLabelProps>(
	({ className, ...props }, ref) => {
		const { size: parentSize } = useStyleContext(SCOPE);
		return (
			<UICheckbox.Label
				className={checkboxLabelStyle({
					parentVariants: {
						size: parentSize,
					},
					class: className,
				})}
				{...props}
				ref={ref}
			/>
		);
	},
);

type ICheckboxIconProps = React.ComponentPropsWithoutRef<typeof UICheckbox.Icon> &
	VariantProps<typeof checkboxIconStyle>;

const CheckboxIcon = React.forwardRef<React.ComponentRef<typeof UICheckbox.Icon>, ICheckboxIconProps>(
	({ className, size, ...props }, ref) => {
		const { size: parentSize } = useStyleContext(SCOPE);

		if (typeof size === "number") {
			return <UICheckbox.Icon ref={ref} {...props} className={checkboxIconStyle({ class: className })} size={size} />;
		}

		if ((props.height !== undefined || props.width !== undefined) && size === undefined) {
			return <UICheckbox.Icon ref={ref} {...props} className={checkboxIconStyle({ class: className })} />;
		}

		return (
			<UICheckbox.Icon
				className={checkboxIconStyle({
					parentVariants: {
						size: parentSize,
					},
					class: className,
					size,
				})}
				{...props}
				ref={ref}
			/>
		);
	},
);

Checkbox.displayName = "Checkbox";
CheckboxIndicator.displayName = "CheckboxIndicator";
CheckboxLabel.displayName = "CheckboxLabel";
CheckboxIcon.displayName = "CheckboxIcon";

export { Checkbox, CheckboxGroup, CheckboxIcon, CheckboxIndicator, CheckboxLabel };
